<template>
  <ax-dialog ref="dialog"
             :title="`Editing ${api}`"
             icon="mdi-pencil-outline"
             tooltip="Edit well"
             size="large"
             persistent
             :disabled="disabled"
             @submitted="onSubmit"
             @shown="onShown">
    <template slot="content">
      <v-card-text v-if="well">
        <v-row>
          <v-col cols="auto mb-3">
            <v-subheader class="ps-0"
                         style="height:auto;">Targets</v-subheader>
            <v-chip-group v-model="target"
                          @change="plotSingleWell"
                          mandatory
                          color="primary">
              <v-chip v-for="t in targets"
                      :key="t.value"
                      :value="t.value">{{ t.text }}</v-chip>
            </v-chip-group>
          </v-col>
          <v-col cols="auto mb-3">
            <v-subheader class="ps-0"
                         style="height:auto;">Methods</v-subheader>
            <v-chip-group v-model="method"
                          @change="plotSingleWell"
                          mandatory
                          color="secondary"
                          column>
              <v-chip v-for="m in methods"
                      :key="m.value"
                      :value="m.value">{{ m.text }}</v-chip>
            </v-chip-group>
          </v-col>
          <v-col>
            <v-subheader class="ps-0"
                         style="height:auto;">Techniques</v-subheader>
            <v-chip-group v-model="technique"
                          @change="plotSingleWell"
                          mandatory
                          color="warning">
              <v-chip v-for="t in techniques"
                      :key="t.value"
                      :value="t.value">{{ t.text }}</v-chip>
            </v-chip-group>
          </v-col>
        </v-row>
        <v-divider />
        <v-toolbar flat
                  tile
                  dense
                  class="px-0"
                  v-if="well">
          <v-toolbar-title>Estimated Ultimate Recovery: {{ totalEUR | numberFormat(round = true) }} ({{ eurUnits }})</v-toolbar-title>
          <v-spacer></v-spacer>
          <ax-button icon="mdi-auto-fix"
                    tooltip="Fix well"
                    @clicked="declineWell"></ax-button>
          <ax-button :icon="showPredictions ? 'mdi-star-four-points' : 'mdi-star-four-points-outline'"
                    tooltip="Toggle predictions"
                    :disabled="method == 'arps1'"
                    @clicked="showPredictions = !showPredictions"></ax-button>
          <ax-button :icon="logAxis ? 'mdi-arrow-expand-down' : 'mdi-arrow-expand-up'"
                    tooltip="Toggle log axis"
                    @clicked="logAxis = !logAxis"></ax-button>
          <ax-button :icon="showTermination ? 'mdi-arrow-expand-left' : 'mdi-arrow-expand-right'"
                    tooltip="Toggle termination"
                    @clicked="showTermination = !showTermination"></ax-button>
        </v-toolbar>
        <vue-plotly id="editing"
                    class="mb-5"
                    :data="plots"
                    :layout="layout"
                    :options="options" />
        <v-row>
          <v-col>
            <v-text-field type="number" filled
                          prefix="Q =" hide-details
                          label="Initial production"
                          v-model.number="well.parameters[`${technique[0] == 'p' ? 'ip' : 'qi'}_${method}_${target}`]"
                          hide-spin-buttons @change="plotSingleWell" />
          </v-col>
          <v-col>
            <v-text-field type="number" filled
                          prefix="D =" hide-details
                          label="Initial decline"
                          v-model.number="well.parameters[`${technique[0] == 'p' ? 'dr' : 'di'}_${method}_${target}`]"
                          hide-spin-buttons @change="plotSingleWell" />
          </v-col>
          <v-col>
            <v-text-field type="number" filled
                          prefix="B =" hide-details
                          label="Hyperbolic exponent"
                          v-model.number="well.parameters[`${technique[0] == 'p' ? 'c' : 'b'}_${method}_${target}`]"
                          hide-spin-buttons @change="plotSingleWell" />
          </v-col>
        </v-row>
      </v-card-text>
    </template>
    <template slot="footer_prefix"> 
      <ax-dialog ref="debugDlg"
                 title="Debug Info"
                 icon="mdi-information-outline"
                 tooltip="Show debug info"
                 size="medium"
                 :cancel="false"
                 :show.sync="debug">
        <template slot="content">
          <v-card-text>
            <pre>{{ well }}</pre>
          </v-card-text>
        </template>
      </ax-dialog>
    </template>
    <template slot="submit_text"> Save </template>
  </ax-dialog>
</template>
<script>
import Predictions from '@/api/predictions'
import VuePlotly from '@statnett/vue-plotly'
import AxDialog from '@/components/dialog'
import AxToggle from '@/components/toggle'
import AxButton from '@/components/button'
export default {
  name: "dlgEditWell",
  props: {
    uuid: { type: String, required: true },
    api: { type: String, required: true },
    targets: { type: Array, required: true },
    methods: { type: Array, required: true },
    techniques: { type: Array, required: true },
    disabled: { type: Boolean, default: false },
  },
  components: {
    VuePlotly,
    AxDialog, AxToggle, AxButton
  },
  data() {
    return {
      debug: false,
      logAxis: true,
      showPredictions: true,
      showTermination: false,
      well: null,

      target: null,
      method: null,
      technique: null,

      parameters: {},
      decline: [],
      plots: [],
      layout: {
        showlegend: true,
        width: 868,
        height: 250,
        legend: { x: 1, y: 1, xanchor: 'right' },
        font: { family: "Roboto, sans-serif" },
        xaxis: { title: { text: "Time (Months)" }, dtick: 12 },
        yaxis: { title: { text: "Production" }, type: "log" },
        hovermode: "closest",
        margin: { l: 60, t: 10, r: 10, b: 60 },
        paper_bgcolor: "#ffffff",
        plot_bgcolor: "#ffffff",
      },
      options: { displayModeBar: false, responsive: true },
      colors: {
        arps1: "#F44336",
        advanced3a: "#2196F3",
        advanced5: "#2196F3",
        multi3a: "#FF9800",
        multi5: "#FF9800",
        hyper5: "#9C27B0",
      },
    }
  },
  watch: {
    showPredictions: function () {
      this.plotSingleWell()
    },
    showTermination: function () {
      this.plotSingleWell()
    },
    logAxis: function () {
      this.plotSingleWell()
    },
  },
  computed: {
    months() {
      return this.last_valid_index(this.well.production[this.target])
    },
    totalEUR() {
      return this.plots.map(item => this.sum(item.y)).reduce((a, b) => a + b, 0)
    },
    eurUnits() {
      return this.targets.find(item => item.value == this.target).units
    },
    targetColor() {
      return this.targets.find(item => item.value == this.target).color
    },
    methodColor() {
      return this.colors[this.method]
    },
    techniqueLabels() {
      if (this.technique == 'curvefit') {
        return {
          Q: `qi_${this.method}_${this.target}`,
          D: `di_${this.method}_${this.target}`,
          B: `b_${this.method}_${this.target}`
        }
      } else if (this.technique == 'polyfit') {
        return {
          Q: `ip_${this.method}_${this.target}`,
          D: `dr_${this.method}_${this.target}`,
          B: `c_${this.method}_${this.target}`
        }
      }
    },
  },
  methods: {
    onShown(val) {
      if (val) {
        this.target = this.targets[0].value
        this.method = this.methods[0].value
        this.technique = this.techniques[0].value

        Predictions.get_well(this.uuid, this.api).then(response => {
          this.well = response.data

          // Object.entries(this.well.parameters).forEach(([key,value]) => {
          //   this.$set(this.parameters, key, parseFloat(value.toFixed(6)))
          // })
          // this.getDeclines()
          this.plotSingleWell()
        })
      }
    },
    onSubmit() {
      this.well.parameters.index = this.api
      this.$refs.dialog.show = false
      this.$emit('submitted', JSON.parse(JSON.stringify(this.well.parameters)))
    },
    getDeclines() {
      return this.old_declineWell(
        this.showTermination ? 600 : this.months + 24,
        this.well.parameters[this.techniqueLabels['Q']],
        this.well.parameters[this.techniqueLabels['D']],
        this.well.parameters[this.techniqueLabels['B']],
      )
    },
    plotSingleWell() {
      // Reset existing data
      const target = this.target
      const method = this.method

      this.plots = []

      // Populate production
      this.plots.push({
        name: 'Production',
        type: "bar",
        x: this.create_array(1, this.months),
        y: this.well.production[target],
        marker: { color: this.targetColor, opacity: 0.5 },
      })

      // Populate predictions
      if (this.showPredictions) {
        if (method != 'arp1') {
          this.plots.push({
            name: 'Prediction',
            type: 'scatter',
            mode: 'lines',
            visible: this.showPredictions,
            x: this.create_array(this.months + 1, this.months + 12),
            y: this.well.predictions[`${target}_${method}`],
            line: { color: this.methodColor, shape: 'spline', dash: 'dot', width: 1 },
          })
        }
      }

      // // Hide curves from beginning to EOL
      let decline = this.getDeclines()
      let predictions = this.well.predictions[`${target}_${method}`].filter(val => val > 0)
      let predLength = this.last_valid_index(predictions)
      
      decline = this.showPredictions
        ? Array(this.months + predLength).fill(null).concat(decline.slice(this.months + predLength))
        : Array(this.months).fill(null).concat(decline.slice(this.months))

      // decline = decline.slice(this.well.production[this.target].length)
      decline = Array(this.months).fill(null).concat(decline.slice(this.months))
      this.plots.push({
        name: 'Decline',
        type: 'scatter',
        mode: 'lines',
        x: this.create_array(1, 600),
        y: decline,
        line: { color: this.methodColor, shape: 'spline', dash: 'solid', width: 1 },
      })

      this.layout.yaxis.type = this.logAxis ? 'log' : 'linear'
    },
    old_declineWell(months, Q, D, B) {
      return Array.from(Array(months), (d, i) => i + 1).map(t => this.old_arps(t, Q, D, B))
    },
    old_arps(t, Q, D, B) {
      if (B == 0) { // Exponential decline
        return Q * Math.exp(-D * t)
      } else if (B == 1) { // Harmonic decline
        return Q / (1 + D * t)
      } else { // Hyperbolic decline
        return Q / ((1 + B * D * t) ** (1 / B))
      }
    },
    declineWell() {
      let payload = {
        well: this.api,
        target: this.target,
        method: this.method,
        technique: this.technique,
      }
      Predictions.decline_well(this.uuid, payload).then(response => {
        this.well.parameters[this.techniqueLabels['Q']] = response.data['Q']
        this.well.parameters[this.techniqueLabels['D']] = response.data['D']
        this.well.parameters[this.techniqueLabels['B']] = response.data['B']
        this.plotSingleWell()
      })
    }
  }
}
</script>